import { MAP_H, MAP_W, TILE, solid, tiles } from './content.ts';

export function tileAt(px: number, py: number): number {
  const tx = Math.floor(px / TILE);
  const ty = Math.floor(py / TILE);
  if (tx < 0 || ty < 0 || tx >= MAP_W || ty >= MAP_H) return 4;
  return tiles[ty][tx];
}

export function tileOpen(tx: number, ty: number): boolean {
  if (tx < 0 || ty < 0 || tx >= MAP_W || ty >= MAP_H) return false;
  return !solid(tiles[ty][tx]);
}

export function solidAt(px: number, py: number): boolean {
  return !tileOpen(Math.floor(px / TILE), Math.floor(py / TILE));
}

export function canStand(x: number, y: number, radius: number): boolean {
  const points = [
    [x - radius, y - radius],
    [x + radius, y - radius],
    [x - radius, y + radius],
    [x + radius, y + radius],
    [x, y],
    [x - radius, y],
    [x + radius, y],
    [x, y - radius],
    [x, y + radius],
  ];
  for (const [px, py] of points) {
    if (solidAt(px, py)) return false;
  }
  return true;
}

export function tryMove(x: number, y: number, dx: number, dy: number, radius: number): { x: number; y: number } {
  if (dx === 0 && dy === 0) return { x, y };
  if (canStand(x + dx, y + dy, radius)) return { x: x + dx, y: y + dy };
  if (dx !== 0 && canStand(x + dx, y, radius)) return { x: x + dx, y };
  if (dy !== 0 && canStand(x, y + dy, radius)) return { x, y: y + dy };
  return { x, y };
}

export function dist(ax: number, ay: number, bx: number, by: number): number {
  return Math.hypot(ax - bx, ay - by);
}

export function angleTo(ax: number, ay: number, bx: number, by: number): number {
  return Math.atan2(by - ay, bx - ax);
}

export function angleDelta(a: number, b: number): number {
  let d = a - b;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return d;
}

/** Orthogonal tile path. Used to keep the diner and the station on one walkable map. */
export function reachableTile(ax: number, ay: number, bx: number, by: number): boolean {
  if (!tileOpen(ax, ay) || !tileOpen(bx, by)) return false;
  const queue: [number, number][] = [[ax, ay]];
  const seen = new Set<number>([ay * MAP_W + ax]);
  for (let i = 0; i < queue.length; i++) {
    const [x, y] = queue[i];
    if (x === bx && y === by) return true;
    for (const [dx, dy] of [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ] as const) {
      const nx = x + dx;
      const ny = y + dy;
      const key = ny * MAP_W + nx;
      if (!tileOpen(nx, ny) || seen.has(key)) continue;
      seen.add(key);
      queue.push([nx, ny]);
    }
  }
  return false;
}
